import { jsPDF } from "jspdf";
import GIF from "gif.js.optimized";
import * as UTIF from "utif";
import ImageProps, { DrawingLayerData, LayerProps, TextLayerData } from "../../types/imageStudio/images";
import { fileToBase64, fileToCanvas, urlToFile } from "../fileHandling";
import { canvasToFile } from "./imagesFunctions";
import { Config } from "../../config/Config";
import { fetchWithAuth } from "../fetchWithAuth";
import { exportMedia } from "../exportMedia";
import { isModelUnavailableStatus, notifyModelUnavailable } from "../../components/ui/ModelUnavailableNotice";
import i18n from "../../i18n";
import { ApiPortalBackend } from "../../apis/ApiPortalBackend";

export type downloadScaleTypes = '1x' | '2x' | '4x';
export const downloadScaleList: downloadScaleTypes[] = ['1x', '2x', '4x' ];
export type ExportFormat = "svg" | "pdf" | "jpg" | "png" | "gif" | "tif";
export const aspectRatioList: aspectRatioTypes[] = ['1:1 (quadratic)', '16:9 (landscape)', '9:16 (portait)','4:3 (standard)'];
export type aspectRatioTypes = '1:1 (quadratic)' | '16:9 (landscape)' | '9:16 (portait)' | '4:3 (standard)';

export interface DownloadImageOptions {
    image: ImageProps;
    format: ExportFormat;
    layers?: LayerProps[];
    fileName?: string;
    backgroundColor?: string | null;
    gifWorkerPath?: string;
    exportFormat?: downloadScaleTypes;
};

const DEFAULT_GIF_WORKER = "/gif.worker.js";

export type qualityListType = 'HD'| 'Standard'| 'Ultra HD';

export const getImagePixelSize = (aspectRatio:aspectRatioTypes, qualityList:qualityListType='Standard') => {
    let imageDimension = {width:100,height:100};
    switch(aspectRatio){
        case '1:1 (quadratic)': {
            if(qualityList === 'Standard'){
                imageDimension.width = 640;
                imageDimension.height = 640;
            }
            else if(qualityList === 'HD'){
                imageDimension.width = 960;
                imageDimension.height = 960;
            }
            else if(qualityList === 'Ultra HD'){
                imageDimension.width = 1280;
                imageDimension.height = 1280;
            }
           
            break;
        }
        case '9:16 (portait)': {
            if(qualityList === 'Standard'){
                imageDimension.width = 480;
                imageDimension.height = 848;
            }
            else if(qualityList === 'HD'){
                imageDimension.width = 720;
                imageDimension.height = 1280;
            }
            else if(qualityList ==='Ultra HD'){
                imageDimension.width = 900;
                imageDimension.height = 1600;
            }
            break;
        }
        case '16:9 (landscape)': {
            if(qualityList === 'Standard'){
                imageDimension.width = 848;
                imageDimension.height = 480;
            }
            else if(qualityList === 'HD'){
                imageDimension.width = 1280;
                imageDimension.height = 720;
            }
            else if(qualityList === 'Ultra HD'){
                imageDimension.width = 1600;
                imageDimension.height = 900;
            }
            break;
        }
        case '4:3 (standard)': {
            if(qualityList === 'Standard'){
                imageDimension.width = 640;
                imageDimension.height = 480;
            }
            else if(qualityList === 'HD'){
                imageDimension.width = 960;
                imageDimension.height = 720;
            }
            else if(qualityList === 'Ultra HD'){
                imageDimension.width = 1280;
                imageDimension.height = 960;
            }
            break;
        }
        default:{
            imageDimension.width = 960;
            imageDimension.height = 960;
            break;
        }
    }
    return imageDimension;
};

const sanitizeFileName = (value?: string | null) => {
    const base = (value || "image").trim() || "image";

    return base
        .replace(/\.[^/.]+$/, "") // remove file extension
        .replace(/[\\/:*?"<>|]+/g, "_");
};

const createDownloadFileName = (
    value?: string | null
) => {
    const baseName = sanitizeFileName(value);

    const uuid =
        typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : Math.random().toString(36).slice(2);

    return `${baseName}-${uuid}`;
};

const parseFontSize = (fontSize?: string, fallback = 24) => {
    if (!fontSize) return fallback;
    const parsed = parseFloat(fontSize);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const escapeXml = (value: string) =>
    value
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&apos;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");


export const loadImage = (src: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(err);
        img.src = src;
    });

export const fileToDataUrl = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ""));
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
export const textLayerToSvgSrc = (width:number, height: number, textLayerData: TextLayerData) => {
    const layerWidth = Math.max(1, Math.round(width || 50));
    const layerHeight = Math.max(1, Math.round(height || 50));
    const text = textLayerData.text || "Text";
    const textAlign = textLayerData.textAlign ?? "center";
    const letterSpacing = textLayerData.letterSpacing ?? 0;
    const shadowBlur = textLayerData.shadowBlur ?? 0;
    const shadowOffsetX = textLayerData.shadowOffsetX ?? 0;
    const shadowOffsetY = textLayerData.shadowOffsetY ?? 0;
    const shadowCss = shadowBlur > 0 ? `${shadowOffsetX}px ${shadowOffsetY}px ${shadowBlur}px ${textLayerData.shadowColor ?? "#000000"}` : "none";
    const radius = Math.max(0, textLayerData.borderRadius ?? 0);
    const radiusCss = `${textLayerData.borderRadiusUnit === "%" ? Math.min(50, radius) : radius}${textLayerData.borderRadiusUnit ?? "px"}`;

    const escapeHtml = (value: string) =>
        value
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");

    const svgBorderStyle = textLayerData.borderStyle ?? "none";
    const svgBorderWidth = Math.max(0, textLayerData.borderWidth ?? 0);
    const svgBorderDash =
        svgBorderStyle === "dashed"
            ? `${svgBorderWidth * 3} ${svgBorderWidth * 2}`
            : svgBorderStyle === "dotted"
                ? `0 ${Math.max(2, svgBorderWidth * 2.2)}`
                : "none";

    const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="${layerWidth}" height="${layerHeight}" viewBox="0 0 ${layerWidth} ${layerHeight}">
            <rect
                x="${svgBorderWidth / 2}"
                y="${svgBorderWidth / 2}"
                width="${Math.max(0, layerWidth - svgBorderWidth)}"
                height="${Math.max(0, layerHeight - svgBorderWidth)}"
                rx="${Math.max(0, (textLayerData.borderRadiusUnit === "%" ? Math.min(layerWidth, layerHeight) * Math.min(50, radius) / 100 : radius) - svgBorderWidth / 2)}"
                fill="${textLayerData.backgroundColor || "transparent"}"
                fill-opacity="${textLayerData.backgroundOpacity ?? 1}"
                stroke="${svgBorderStyle !== "none" && svgBorderWidth > 0 ? textLayerData.borderColor || "#ffffff" : "none"}"
                stroke-opacity="${textLayerData.borderOpacity ?? 1}"
                stroke-width="${svgBorderWidth}"
                stroke-dasharray="${svgBorderDash}"
                stroke-linecap="${svgBorderStyle === "dotted" ? "round" : "butt"}"
            />
            <foreignObject x="0" y="0" width="${layerWidth}" height="${layerHeight}">
                <div
                    xmlns="http://www.w3.org/1999/xhtml"
                    style="
                        width:100%;
                        height:100%;
                        box-sizing:border-box;
                        padding:6px;
                        display:flex;
                        align-items:center;
                        justify-content:center;
                        overflow:hidden;
                    "
                >
                    <div
                        style="
                            width:100%;
                            max-width:100%;
                            color:${textLayerData.fontColor || "#ffffff"};
                            font-family:${textLayerData.fontFamily || "Arial"};
                            font-size:${textLayerData.fontSize || "24px"};
                            font-weight:${textLayerData.style?.includes("bold") ? "bold" : "normal"};
                            font-style:${textLayerData.style?.includes("italic") ? "italic" : "normal"};
                            text-decoration:${textLayerData.style?.includes("underline") ? "underline" : "none"};
                            text-align:${textAlign};
                            letter-spacing:${letterSpacing}px;
                            text-shadow:${shadowCss};
                            line-height:1.2;
                            white-space:pre-wrap;
                            overflow-wrap:anywhere;
                            word-break:break-word;
                            opacity:${textLayerData.textOpacity ?? 1};
                        "
                    >
                        ${escapeHtml(text)}
                    </div>
                </div>
            </foreignObject>
        </svg>
    `;

    const src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    return src;
}
const ensureImageSrc = async (img?: ImageProps | null): Promise<string | null> => {
    if (!img) return null;
    if (img.src) return img.src;
    if (img.file) return fileToDataUrl(img.file);
    return null;
};

const getLayerRenderableImage = (layer: LayerProps): ImageProps | null => {
    if (layer.type === "image") {
        return layer.imageEdit || layer.imageData || null;
    }

    if (layer.type === "ai-variation") {
        if (layer.imageEdit) return layer.imageEdit;
        if (layer.imageData) return layer.imageData;
        if (layer.variations?.length) return layer.variations[0];
    }

    return null;
};

const sortVisibleLayers = (layers?: LayerProps[]) => {
    return [...(layers || [])]
        .filter((layer) => !layer.hidden)
        .sort((a, b) => {
            const aNum = Number(a.orderNumber);
            const bNum = Number(b.orderNumber);

            if (Number.isFinite(aNum) && Number.isFinite(bNum)) {
                return aNum - bNum;
            }

            return String(a.orderNumber).localeCompare(String(b.orderNumber), undefined, {
                numeric: true,
                sensitivity: "base"
            });
        });
};

const applyLayerTransform = (
    ctx: CanvasRenderingContext2D,
    layer: LayerProps,
    extraRotation = 0
) => {
    const rotation = ((layer.rotation ?? 0) + extraRotation) * (Math.PI / 180);
    const flipX = layer.flipHorizontal ? -1 : 1;
    const flipY = layer.flipVertical ? -1 : 1;

    ctx.translate(layer.position.x + layer.width / 2, layer.position.y + layer.height / 2);
    ctx.scale(flipX, flipY);

    if (rotation !== 0) {
        ctx.rotate(rotation);
    }
    ctx.translate(-layer.width / 2, -layer.height / 2);
};

const roundedRect = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    radius = 0
) => {
    if (radius <= 0) {
        ctx.rect(x, y, w, h);
        ctx.closePath();
        return;
    }

    const r = Math.min(radius, w / 2, h / 2);
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
};
const getBorderRadiusPx = (value: number | undefined, unit: "px" | "%" | undefined, width: number, height: number) => {
    const safeValue = Math.max(0, Number(value) || 0);
    if (unit === "%") return Math.min(width, height) * Math.min(50, safeValue) / 100;
    return Math.min(safeValue, width / 2, height / 2);
};

const wrapText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number, letterSpacing = 0): string[] => {
    const paragraphs = text.split("\n");
    const lines: string[] = [];

    paragraphs.forEach(paragraph => {
        const words = paragraph
            .split(/\s+/)
            .filter(Boolean)
            .flatMap(word =>
                measureSpacedText(ctx, word, letterSpacing) > maxWidth
                    ? splitWordToFit(ctx, word, maxWidth, letterSpacing)
                    : [word]
            );
        if (!words.length) {
            lines.push("");
            return;
        }

        let currentLine = words[0];
        for (let i = 1; i < words.length; i++) {
            const testLine = `${currentLine} ${words[i]}`;
            if (measureSpacedText(ctx, testLine, letterSpacing) <= maxWidth) currentLine = testLine;
            else {
                lines.push(currentLine);
                currentLine = words[i];
            }
        }
        lines.push(currentLine);
    });

    return lines;
};

const fitFontSizeToBox = (
    ctx: CanvasRenderingContext2D,
    textData: TextLayerData,
    width: number,
    height: number
) => {
    let fontSize = parseFontSize(textData.fontSize, 24);
    const isBold = textData.style?.includes("bold");
    const isItalic = textData.style?.includes("italic");

    if (!textData.autoFit) {
        ctx.font = `${isItalic ? "italic " : ""}${isBold ? "bold " : ""}${fontSize}px ${textData.fontFamily || "Arial"}`;
        return fontSize;
    }

    while (fontSize > 6) {
        ctx.font = `${isItalic ? "italic " : ""}${isBold ? "bold " : ""}${fontSize}px ${textData.fontFamily || "Arial"}`;
        const letterSpacing = textData.letterSpacing ?? 0;
        const lines = wrapText(ctx, textData.text || "", Math.max(1, width - 12),letterSpacing);
        const lineHeight = fontSize * 1.2;
        const totalHeight = lines.length * lineHeight + 12;

        if (totalHeight <= height) {
            return fontSize;
        }

        fontSize -= 1;
    }

    return 6;
};
const measureSpacedText = (ctx: CanvasRenderingContext2D, text: string, spacing: number) => 
    Math.max(0, ctx.measureText(text).width + Math.max(0, text.length - 1) * spacing);

const splitWordToFit = (ctx: CanvasRenderingContext2D, word: string, maxWidth: number, letterSpacing: number): string[] => {
    const parts: string[] = [];
    let current = "";

    for (const char of word) {
        const next = current + char;

        if (current && measureSpacedText(ctx, next, letterSpacing) > maxWidth) {
            parts.push(current);
            current = char;
        } else {
            current = next;
        }
    }

    if (current) parts.push(current);
    return parts;
};
const drawSpacedText = (ctx: CanvasRenderingContext2D, text: string, x: number, y: number, spacing: number) => {
    if (!spacing) {
        ctx.fillText(text, x, y);
        return;
    }
    let currentX = x;
    for (const char of text) {
        ctx.fillText(char, currentX, y);
        currentX += ctx.measureText(char).width + spacing;
    }
};
const drawJustifiedText = (ctx: CanvasRenderingContext2D, text: string, x: number, y: number, maxWidth: number, letterSpacing: number) => {
    const words = text.split(/\s+/).filter(Boolean);
    if (words.length < 2) {
        drawSpacedText(ctx, text, x, y, letterSpacing);
        return;
    }

    const wordsWidth = words.reduce((sum, word) => sum + measureSpacedText(ctx, word, letterSpacing), 0);
    const gap = Math.max(0, (maxWidth - wordsWidth) / (words.length - 1));
    let currentX = x;

    words.forEach((word, index) => {
        drawSpacedText(ctx, word, currentX, y, letterSpacing);
        currentX += measureSpacedText(ctx, word, letterSpacing);
        if (index < words.length - 1) currentX += gap;
    });
};
const drawTextLayer = async (
    ctx: CanvasRenderingContext2D,
    layer: LayerProps,
    textData: TextLayerData
) => {
    //load font
    const fontSize = parseFontSize(textData.fontSize, 16);
    const fontFamily = textData.fontFamily || "Arial";

    if (typeof document !== "undefined" && document.fonts) {
        try {
            await document.fonts.load(`${fontSize}px "${fontFamily}"`);
        } catch {
            console.log('Unable to load font. Use font of browsers fallback')
        }
    }
    const svgSrc = textLayerToSvgSrc(layer.width, layer.height, textData);
    const textImage = await loadImage(svgSrc);
    
    ctx.save();
    applyLayerTransform(ctx, layer, textData.rotation ?? 0);

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(textImage, 0, 0, layer.width, layer.height);
    ctx.restore();
};

const drawDrawingLayer = async (
    ctx: CanvasRenderingContext2D,
    layer: LayerProps,
    drawingData: DrawingLayerData
) => {
    const rasterSrc = drawingData.rasterSrc;
    if (!rasterSrc) return;

    const img = await loadImage(rasterSrc);

    ctx.save();
    applyLayerTransform(ctx, layer, drawingData.rotation ?? 0);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, layer.width, layer.height);
    ctx.restore();
};

const drawImageLayer = async (
    ctx: CanvasRenderingContext2D,
    layer: LayerProps,
    layerImage: ImageProps
) => {
    const src = await ensureImageSrc(layerImage);
    if (!src) return;

    const img = await loadImage(src);
    const style = layer.imageLayerStyle ?? {};
    const borderRadius = getBorderRadiusPx(style.borderRadius, style.borderRadiusUnit, layer.width, layer.height);

    ctx.save();
    applyLayerTransform(ctx, layer);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    if (
        style.backgroundColor &&
        style.backgroundColor !== "transparent" &&
        (style.backgroundOpacity ?? 0) > 0
    ) {
        ctx.save();
        ctx.globalAlpha = style.backgroundOpacity ?? 1;
        ctx.fillStyle = style.backgroundColor;
        ctx.beginPath();
        roundedRect(ctx, 0, 0, layer.width, layer.height, borderRadius);
        ctx.fill();
        ctx.restore();
    }

    ctx.save();
    ctx.globalAlpha = style.imageOpacity ?? 1;
    ctx.beginPath();
    roundedRect(ctx, 0, 0, layer.width, layer.height, borderRadius);
    ctx.clip();

    const crop = layer.cropRect;
    if (crop && crop.w > 0 && crop.h > 0) {

        ctx.drawImage(
            img,
            crop.x,
            crop.y,
            crop.w,
            crop.h,
            0,
            0,
            layer.width,
            layer.height
        );
    } else {
        ctx.drawImage(img, 0, 0, layer.width, layer.height);
    }
    ctx.restore();

    if (
        (style.borderStyle ?? "none") !== "none" &&
        (style.borderWidth ?? 0) > 0 &&
        (style.borderOpacity ?? 0) > 0
    ) {
        ctx.save();
        ctx.globalAlpha = style.borderOpacity ?? 1;
        ctx.strokeStyle = style.borderColor || "#ffffff";
        ctx.lineWidth = style.borderWidth ?? 1;

        if (style.borderStyle === "dashed") {
            ctx.lineCap = "butt";
            ctx.lineJoin = "miter";
            ctx.setLineDash([20,4]);

            const borderWidth = style.borderWidth ?? 1;
            const inset = borderWidth / 2;

            ctx.beginPath();
            roundedRect(ctx, inset, inset, layer.width - borderWidth, layer.height - borderWidth, Math.max(0, borderRadius - inset));
            ctx.stroke();
        } else if (style.borderStyle === "dotted") {
            const bw = style.borderWidth ?? 1;
            ctx.lineCap = "round";
            ctx.lineJoin = "round";
            ctx.setLineDash([0, Math.max(2, bw * 2.2)]);

            const borderWidth = style.borderWidth ?? 1;
            const inset = borderWidth / 2;

            ctx.beginPath();
            roundedRect(ctx, inset, inset, layer.width - borderWidth, layer.height - borderWidth, Math.max(0, borderRadius - inset));
            ctx.stroke();
        } else {
            ctx.lineCap = "butt";
            ctx.lineJoin = "miter";
            ctx.setLineDash([]);

            const borderWidth = style.borderWidth ?? 1;
            const inset = borderWidth / 2;

            ctx.beginPath();
            roundedRect(ctx, inset, inset, layer.width - borderWidth, layer.height - borderWidth, Math.max(0, borderRadius - inset));
            ctx.stroke();
        }

        ctx.restore();
    }

    ctx.restore();
};

export const composeImageWithLayers = async (
    image: ImageProps,
    layers?: LayerProps[],
    backgroundColor: string | null = null
): Promise<HTMLCanvasElement> => {
    const baseSrc = await ensureImageSrc(image);
    if (!baseSrc) {
        throw new Error("Das Basisbild hat keine gültige src/file.");
    }

    const baseImage = await loadImage(baseSrc);
    const width = image.width || baseImage.naturalWidth;
    const height = image.height || baseImage.naturalHeight;

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
        throw new Error("Canvas-Kontext konnte nicht erstellt werden.");
    }

    if (backgroundColor) {
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, width, height);
    }

    ctx.drawImage(baseImage, 0, 0, width, height);

    const visibleLayers = sortVisibleLayers(layers);

    for (const layer of visibleLayers) {
        if (layer.type === "text" && layer.textData) {
            await drawTextLayer(ctx, layer, layer.textData);
            continue;
        }

        if (layer.type === "drawings" && layer.drawingData) {
            await drawDrawingLayer(ctx, layer, layer.drawingData);
            continue;
        }

        if (layer.type === "image" || layer.type === "ai-variation") {
            const renderable = getLayerRenderableImage(layer);
            if (renderable) {
                await drawImageLayer(ctx, layer, renderable);
            }
        }
    }

    return canvas;
};

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> =>
    new Promise((resolve, reject) => {
        // eslint-disable-next-line no-restricted-syntax -- Blob wird an exportMedia() zur Kennzeichnung/zum Download uebergeben (siehe downloadPng/downloadJpg unten)
        canvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error(`Blob-Erstellung für ${type} fehlgeschlagen.`));
                return;
            }
            resolve(blob);
        }, type, quality);
    });

const canvasToSvgBlob = async (canvas: HTMLCanvasElement) => {
    // eslint-disable-next-line no-restricted-syntax -- wird in ein SVG eingebettet, das anschliessend via downloadSvg() an exportMedia() geht
    const dataUrl = canvas.toDataURL("image/png");
    const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="${canvas.width}" height="${canvas.height}" viewBox="0 0 ${canvas.width} ${canvas.height}">
    <image href="${escapeXml(dataUrl)}" width="${canvas.width}" height="${canvas.height}" />
</svg>`.trim();

    return new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
};

const downloadPng = async (canvas: HTMLCanvasElement, fileName: string) => {
    const blob = await canvasToBlob(canvas, "image/png");
    await exportMedia(blob, fileName);
};

const downloadJpg = async (canvas: HTMLCanvasElement, fileName: string) => {
    const jpgCanvas = document.createElement("canvas");
    jpgCanvas.width = canvas.width;
    jpgCanvas.height = canvas.height;

    const ctx = jpgCanvas.getContext("2d");
    if (!ctx) throw new Error("JPG-Canvas konnte nicht erstellt werden.");

    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, jpgCanvas.width, jpgCanvas.height);
    ctx.drawImage(canvas, 0, 0);

    const blob = await canvasToBlob(jpgCanvas, "image/jpeg", 0.95);
    await exportMedia(blob, fileName);
};

const downloadSvg = async (canvas: HTMLCanvasElement, fileName: string) => {
    const blob = await canvasToSvgBlob(canvas);
    await exportMedia(blob, fileName);
};

const downloadPdf = async (canvas: HTMLCanvasElement, fileName: string) => {
    const orientation = canvas.width >= canvas.height ? "landscape" : "portrait";
    const pdf = new jsPDF({
        orientation,
        unit: "pt",
        format: [canvas.width, canvas.height]
    });

    // eslint-disable-next-line no-restricted-syntax -- PDF-Export ueber jsPDF (eigener Downloadpfad via pdf.save(), PDF ist ohnehin kein cProxy-/mark-Format)
    const imgData = canvas.toDataURL("image/png");
    pdf.addImage(imgData, "PNG", 0, 0, canvas.width, canvas.height);
    pdf.save(fileName);
};

const downloadGif = async (
    canvas: HTMLCanvasElement,
    fileName: string,
    workerPath: string
) => {
    const gif = new GIF({
        workers: 2,
        quality: 10,
        workerScript: workerPath,
        width: canvas.width,
        height: canvas.height
    });

    gif.addFrame(canvas, { copy: true, delay: 1000 });

    const blob: Blob = await new Promise((resolve, reject) => {
        let finished = false;

        gif.on("finished", (result: Blob) => {
            finished = true;
            resolve(result);
        });

        gif.on("abort", () => {
            reject(new Error("GIF-Erstellung wurde abgebrochen."));
        });

        gif.on("error", (error: unknown) => {
            reject(
                error instanceof Error
                    ? error
                    : new Error("GIF-Erstellung fehlgeschlagen.")
            );
        });

        try {
            gif.render();
        } catch (error) {
            reject(error);
        }

        setTimeout(() => {
            if (!finished) {
                reject(new Error(`GIF-Worker konnte nicht geladen werden: ${workerPath}`));
            }
        }, 8000);
    });

    await exportMedia(blob, fileName);
};

const downloadTif = async (canvas: HTMLCanvasElement, fileName: string) => {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas-Kontext für TIF fehlt.");

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const rgba = new Uint8Array(imageData.data);

    const buffer = UTIF.encodeImage(rgba, imageData.width, imageData.height);
    const blob = new Blob([buffer], { type: "image/tiff" });
    await exportMedia(blob, fileName);
};

export const downloadImageInFormat = async ({
    image,
    format,
    layers = [],
    fileName='image',
    backgroundColor = null,
    gifWorkerPath = DEFAULT_GIF_WORKER,
    exportFormat='1x'
}: DownloadImageOptions) => {
    const safeBaseName = createDownloadFileName(fileName || image.title || "image");
    const canvas = await composeImageWithLayers(image, layers, format === "jpg" ? "#ffffff" : backgroundColor);
    
    switch (format) {
        case "png":{
            const canvasUpscaled = await upscaleImage(exportFormat,canvas,safeBaseName);
            await downloadPng(canvasUpscaled, `${safeBaseName}.png`);
            return;}

        case "jpg":{
            const canvasUpscaled = await upscaleImage(exportFormat,canvas,safeBaseName);
            await downloadJpg(canvasUpscaled, `${safeBaseName}.jpg`);
            return;}

        case "svg":{
            await downloadSvg(canvas, `${safeBaseName}.svg`);
            return;}

        case "pdf":{
            await downloadPdf(canvas, `${safeBaseName}.pdf`);
            return;}

        case "gif":{
            await downloadGif(canvas, `${safeBaseName}.gif`, gifWorkerPath);
            return;}
        case "tif":{
            await downloadTif(canvas, `${safeBaseName}.tif`);
            return;}

        default:
            throw new Error(`Format ${format} wird nicht unterstützt.`);
    }
};

export const upscaleImage = async (
    type: downloadScaleTypes,
    canvas: HTMLCanvasElement,
    filename: string
): Promise<HTMLCanvasElement> => {
    if (type === "1x") {
        return canvas;
    }

    const baseUrl = Config.json.AiServiceProxyUrl;

    const file = await canvasToFile(canvas, `${filename}.png`);
    const base64Image = await fileToBase64(file);

    if (!base64Image) {
        return canvas;
    }

    const path =
        type === "4x"
            ? "generate/image-upscale-4x"
            : "generate/image-upscale-2x";

    const body = {
        positive_prompt: "",
        image: base64Image
    };

    // upscaleImage() is a plain async function, not a React hook, so useTrackedRun() cannot be
    // called here - the run lifecycle is driven directly via ApiPortalBackend instead.
    const workspaceId = await ApiPortalBackend.GetDefaultWorkspaceId();
    if (!workspaceId) {
        throw new Error("Kein Workspace gefunden - Upscale kann nicht abgerechnet werden.");
    }

    const runResult = await ApiPortalBackend.CreateRun({ workspaceId, type: 'image_gen', estimatedCost: 0 });
    if (!runResult.ok) {
        // Fail closed: never call cProxy's paid upscale endpoint without a tracked run.
        throw new Error(
            // Kein Credit-Wortlaut mehr (Masterplan Paket 3) - Kontingent-Text wie im Dialog.
            runResult.reason === 'insufficient_balance'
                ? i18n.t('common:quota.body')
                : "Upscale-Run konnte nicht gestartet werden."
        );
    }
    const runId = runResult.run.id;

    const jobResponse = await fetchWithAuth(`${baseUrl}/${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
    });

    if (!jobResponse.ok) {
        await ApiPortalBackend.FailRun(runId, `Upscale job creation failed (HTTP ${jobResponse.status})`);
        // Gesperrtes Modell: Nutzer informieren, statt still das nicht hochskalierte Bild zu liefern.
        if (isModelUnavailableStatus(jobResponse.status)) notifyModelUnavailable();
        return canvas;
    }

    const jobData = await jobResponse.json();
    const jobID = jobData.job_id;

    if (!jobID) {
        await ApiPortalBackend.FailRun(runId, "Upscale job creation returned no job_id");
        return canvas;
    }

    const pollResult = async (): Promise<string | null> => {
        for (let attempt = 0; attempt < 90; attempt++) {
            const statusResponse = await fetchWithAuth(`${baseUrl}/status/${jobID}`);

            if (!statusResponse.ok) {
                return null;
            }

            const statusData = await statusResponse.json();
            const status = statusData.status;

            if (status === "finished") {
                const resultResponse = await fetchWithAuth(`${baseUrl}/result/${jobID}`);

                if (!resultResponse.ok) {
                    return null;
                }

                const resultData = await resultResponse.json();

                if (Array.isArray(resultData) && resultData.length > 0) {
                    return String(resultData[0]).replaceAll("\\", "/");
                }

                return null;
            }

            if (status === "timeout" || status === "error") {
                return null;
            }

            await new Promise((resolve) => setTimeout(resolve, 2000));
        }

        return null;
    };

    const resultUrl = await pollResult();

    if (!resultUrl) {
        await ApiPortalBackend.FailRun(runId, "Upscale polling timed out or failed");
        return canvas;
    }

    const imgFile = await urlToFile(
        resultUrl,
        resultUrl.split("/").slice(-1)[0] || `${filename}.png`
    );

    await ApiPortalBackend.CompleteRun(runId, { cost: 0 });

    return await fileToCanvas(imgFile);
};

export default downloadImageInFormat;

export const convertFileToPngFile = async (
    file: File,
    filename?: string
): Promise<File> => {

    const canvas = await fileToCanvas(file);

    const blob = await canvasToBlob(canvas,"image/png")

    return new File(
        [blob],
        filename ?? file.name.replace(/\.[^/.]+$/, ".png"),
        {
            type: "image/png"
        }
    );
};

export const createImageThumbnailBlob = async (
    image: ImageProps,
    layers: LayerProps[] = [],
    size = 200,
    backgroundColor: string | null = null
): Promise<Blob> => {
    const composedCanvas = await composeImageWithLayers(
        image,
        layers,
        backgroundColor
    );

    const thumbnailCanvas = document.createElement("canvas");
    thumbnailCanvas.width = size;
    thumbnailCanvas.height = size;

    const ctx = thumbnailCanvas.getContext("2d");
    if (!ctx) {
        throw new Error("Thumbnail canvas context could not be created.");
    }

    ctx.clearRect(0, 0, size, size);

    const scale = Math.min(
        size / composedCanvas.width,
        size / composedCanvas.height
    );

    const drawWidth = composedCanvas.width * scale;
    const drawHeight = composedCanvas.height * scale;

    const x = (size - drawWidth) / 2;
    const y = (size - drawHeight) / 2;

    ctx.drawImage(
        composedCanvas,
        x,
        y,
        drawWidth,
        drawHeight
    );

    return await canvasToBlob(thumbnailCanvas, "image/png");
};

export const createImageThumbnailFile = async (
    image: ImageProps,
    layers: LayerProps[] = [],
    filename = "thumbnail.png",
    size = 200,
    backgroundColor: string | null = null
): Promise<File> => {
    const blob = await createImageThumbnailBlob(
        image,
        layers,
        size,
        backgroundColor
    );

    return new File(
        [blob],
        filename,
        { type: "image/png" }
    );
};